import { BLACK_COLOR, DEFAULT_COLORS, WHITE_COLOR } from '@shared/constants'
import { Editor, useEditorState } from '@tiptap/react'
import { Button, ColorPicker, Flex, Popover, Tooltip } from 'antd'
import { BrushSquare } from 'iconsax-react'
import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

import type { ColorPickerProps } from 'antd'

type Props = {
  editor: Editor
}

export const BackgroundColorButton = ({ editor }: Props) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const { styles } = useStyles()

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const backgroundColorAttr =
        ctx.editor.getAttributes('textStyle').backgroundColor

      return {
        backgroundColor: backgroundColorAttr || ''
      }
    }
  })

  const currentColor = editorState.backgroundColor || WHITE_COLOR

  const onColorChange: ColorPickerProps['onChange'] = (color) => {
    const hexColor = color.toHexString()
    editor
      .chain()
      .focus()
      .setMark('textStyle', { backgroundColor: hexColor })
      .run()
  }

  const onClear = () => {
    editor.chain().focus().unsetMark('textStyle').run()
    setOpen(false)
  }

  const onClose = () => {
    setOpen(false)
  }

  const colorPickerContent = (
    <Flex vertical className={styles.colorPickerContent}>
      <ColorPicker
        value={currentColor}
        onChange={onColorChange}
        format="hex"
        showText
        disabledAlpha
        presets={[
          {
            label: t('editor.default'),
            colors: [WHITE_COLOR, BLACK_COLOR]
          },
          {
            label: t('editor.common'),
            colors: DEFAULT_COLORS
          }
        ]}
      />
      <Flex gap="8px" className={styles.buttonContainer}>
        <Button size="small" onClick={onClear} className={styles.button}>
          Clear
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={onClose}
          className={styles.button}
        >
          Done
        </Button>
      </Flex>
    </Flex>
  )

  return (
    <Tooltip title={t('editor.background_color')}>
      <Popover
        content={colorPickerContent}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottom"
      >
        <Button
          icon={<BrushSquare size={14} />}
          type={editorState.backgroundColor ? 'primary' : 'default'}
        />
      </Popover>
    </Tooltip>
  )
}
